import { create } from "zustand";
import User from "../model/User.model";
import {
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  signIn,
  signOut,
} from "aws-amplify/auth";
import {
  getUserLogged,
  isUserLogged,
  resetUserLoggedData,
  storeUserLoggedData,
} from "../services/storage";
import { IPayloadSignup, signup } from "../network/actions/auth.actions";
import { normalizeEmail } from "../services/email";
import { getCurrentUserInfo } from "../services/auth";

interface AuthState {
  user: User | undefined;
  login: (email: string, password: string) => Promise<any>;
  signup: (data: IPayloadSignup) => Promise<any>;
  confirmSignup: (email: string, code: string) => Promise<any>;
  resendVerificationCode: (email: string) => Promise<any>;
  logout: () => Promise<void>;
}

const getUsernameFromEmail = (email: string): string => normalizeEmail(email)

const useAuthStore = create<AuthState>((set) => ({
  user: getUserLogged(),
  login: async (email: string, password: string) => {
    // Login to Cognito AWS
    const credentials = { username: getUsernameFromEmail(email), password };
    try {
      await signIn(credentials);
    } catch (error: any) {
      if (error?.name !== "UserAlreadyAuthenticatedException") throw error;
      await signOut();
      await signIn(credentials);
    }
    const [authInfo, session] = await Promise.all([
      getCurrentUserInfo(),
      fetchAuthSession(),
    ]);
    const accessToken = session.tokens?.accessToken;
    if (!accessToken) throw new Error("Missing access token after sign-in");
    const responseUser = User.factoryCognitoUser(authInfo.username, authInfo.attributes);
    // Store data locally
    storeUserLoggedData(accessToken.toString(), accessToken.payload.exp, responseUser);
    set((state) => ({
      ...state,
      user: responseUser,
    }))
  },
  logout: async () => {
    try {
      await signOut();
    } finally {
      resetUserLoggedData();
      set((state) => ({
        ...state,
        user: undefined,
      }))
    }
  },
  signup: async (data: IPayloadSignup) => {
    // SignUp to Cognito AWS
    await signup(data)
  },
  resendVerificationCode: async (email: string) => {
    await resendSignUpCode({ username: getUsernameFromEmail(email) })
  },
  confirmSignup: async (email: string, code: string) => {
    await confirmSignUp({
      username: getUsernameFromEmail(email),
      confirmationCode: code,
      options: { forceAliasCreation: true },
    });
  },
}));

export { useAuthStore };
